import { ReactNode, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { AppliedFilters, EmptyState, PeriodDate, SortBy } from '@/components'
import { DEFAULT_LIMIT_PAGE } from '@/constants'
import { formatCurrency, formatDate, useParamsState } from '@/helpers'
import { useShipmentInvoiceList, useTransportCompanyList } from '@/services'
import { ShipmentInvoiceProps } from '@/types'
import {
  Button,
  EditIcon,
  EyeIcon,
  FilterIcon,
  InputSearch,
  Pagination,
  Select,
  Tab,
  TableColumns,
  TableColumnsColumnProps,
  Tag,
  Text,
  Tooltip,
} from '@mercai/clever'
import { addDays, format } from 'date-fns'

import {
  ShipmentInvoiceDrawerFilter,
  ShipmentInvoiceDrawerForm,
  ShipmentInvoiceDrawerShow,
} from './components'

import { getExpectedPaidClasses } from './table-shipment-invoice.constants'

import { TableShipmentInvoiceProps } from './table-shipment-invoice.type'

import { ShipmentInvoiceDrawerRequestNew } from './components/drawer-request-new'
import styles from './table-shipment-invoice.module.css'

export const TableShipmentInvoice = ({
  isOpenNewShipmentInvoice,
  onHandleOpenNewShipmentInvoice,
}: TableShipmentInvoiceProps) => {
  const { t: tEnums } = useTranslation('enums')
  const { t } = useTranslation('screenSupplierOrderManagementPage')

  const [filter, setFilter] = useParamsState({
    page: '1',
    limit: `${DEFAULT_LIMIT_PAGE}`,
    search: '',
    status: '',
    ordering: '',
  })

  const [isOpenDrawerFilter, setIsOpenDrawerFilter] = useState(false)

  const [selectedTab, setSelectedTab] = useState<string | undefined>(undefined)

  const [params, setParams] = useParamsState({
    uuid: '',
  })

  const { data: transportCompanyData } = useTransportCompanyList(
    {
      params: { limit: 100, ordering: 'name' },
    },
    isOpenDrawerFilter || !!filter.transportCompany_Email,
  )

  const { data, isLoading } = useShipmentInvoiceList(
    {
      ...filter,
      uuid: '',
      showUuid: '',
      limit: filter.limit || `${DEFAULT_LIMIT_PAGE}`,
    },
    true,
  )

  const [totalPages, setTotalPages] = useState(1)

  const handleOpenDrawerForm = (uuid: string) => {
    setParams({ uuid })
  }

  const handleOpenDrawerShow = (uuid: string) => {
    setParams({ showUuid: uuid })
  }

  const getContentTotalFiltered = () => {
    const activeFilters = Object.keys(filter)?.filter(
      (key) =>
        !!filter[key] &&
        key !== 'page' &&
        key !== 'limit' &&
        key !== 'uuid' &&
        key !== 'showUuid' &&
        key !== 'expectedPaymentDateBefore' &&
        key !== 'expectedPaymentDateAfter' &&
        key !== 'ordering',
    ).length

    if (activeFilters > 0) {
      return (
        <Tag variant="primary" model="light" size="small">
          {activeFilters}
        </Tag>
      )
    }

    return ''
  }

  const handleFilter = (changedKey: string, newValue: string | number) => {
    if (changedKey === 'page') {
      setFilter({ ...filter, [changedKey]: `${newValue}` })
    } else {
      setFilter({ ...filter, [changedKey]: `${newValue}`, page: '1' })
    }
  }

  const onHandleSortBy = (ordering: string) => {
    if (ordering === filter.ordering) {
      return handleFilter('ordering', '')
    }

    handleFilter('ordering', ordering)
  }

  const onChangeFilter = (newFilter: Record<string, string>) => {
    if (
      filter?.expectedPaymentDateBefore !==
        newFilter?.expectedPaymentDateBefore ||
      filter?.expectedPaymentDateAfter !== newFilter?.expectedPaymentDateAfter
    ) {
      setSelectedTab('period')
    }
    setFilter(newFilter)
  }

  const statuses: Record<string, ReactNode> = {
    created: (
      <Tag variant="basic" model="light">
        {tEnums('shipment_invoice_status_created')}
      </Tag>
    ),
    pending: (
      <Tag variant="warning" model="light">
        {tEnums('shipment_invoice_status_pending')}
      </Tag>
    ),
    request_review: (
      <Tag variant="info" model="light">
        {tEnums('shipment_invoice_status_request_review')}
      </Tag>
    ),
    paid: (
      <Tag variant="success" model="light">
        {tEnums('shipment_invoice_status_paid')}
      </Tag>
    ),
    cancelled: (
      <Tag variant="danger" model="light">
        {tEnums('shipment_invoice_status_cancelled')}
      </Tag>
    ),
  }

  const SaleOrdersColumn = ({ record }: { record: ShipmentInvoiceProps }) => {
    const referencesString =
      record?.shipments?.map((link) => link.order).join(', ') || '-'
    return (
      <Tooltip text={referencesString} widthText="18.75rem">
        {referencesString.length > 30
          ? referencesString.slice(0, 30) + '...'
          : referencesString}
      </Tooltip>
    )
  }

  const columns: TableColumnsColumnProps<ShipmentInvoiceProps>[] = [
    {
      title: t('tabShipmentInvoiceTableShipmentInvoiceTransportCompanyColumn'),
      key: 'transportCompanyUuid',
      render: ({ record }) => (
        <div className={styles['column-supplier']}>
          <Tooltip
            text={record?.transportCompany?.name || '-'}
            orientation="top"
          >
            {record?.transportCompany?.name || '-'}
          </Tooltip>
        </div>
      ),
    },
    {
      title: t('tabShipmentInvoiceTableShipmentInvoiceSaleOrdersColumn'),
      key: 'sos',
      width: '12rem',
      render: ({ record }) => <SaleOrdersColumn record={record} />,
    },
    {
      title: t(
        'tabShipmentInvoiceTableShipmentInvoiceExpectedPaymentDateColumn',
      ),
      key: 'expectedPaymentDate',
      width: '10rem',
      render: ({ record }) => (
        <div
          className={getExpectedPaidClasses(
            record?.expectedPaymentDate || '',
            record?.transactions?.[0]?.paidAt,
          )}
        >
          {record?.expectedPaymentDate
            ? formatDate(record?.expectedPaymentDate)
            : '-'}
        </div>
      ),
    },
    {
      title: t('tabShipmentInvoiceTableShipmentInvoiceValueColumn'),
      key: 'value',
      width: '11rem',
      render: ({ record }) => {
        return (
          <div>
            {record?.value
              ? formatCurrency(
                  record?.value,
                  'es-MX',
                  record?.currency || 'MXN',
                )
              : '-'}
          </div>
        )
      },
    },
    {
      title: t('tabShipmentInvoiceTableShipmentInvoiceCurrencyColumn'),
      key: 'currency',
      dataKey: 'currency',
      width: '5.6rem',
    },
    {
      title: t('tabShipmentInvoiceTableShipmentInvoiceDestinationBankColumn'),
      key: 'destinationBank',
      width: '10rem',
      render: ({ record }) => record?.transactions?.[0]?.destinationBank || '-',
    },
    {
      title: t('tabShipmentInvoiceTableShipmentInvoiceTypeColumn'),
      key: 'type',
      width: '8.5rem',
      render: ({ record }) =>
        record?.type ? tEnums(`shipment_invoice_type_${record?.type}`) : '-',
    },
    {
      title: t('tabShipmentInvoiceTableShipmentInvoicePaymentPriorityColumn'),
      key: 'paymentPriority',
      width: '11rem',
      render: ({ record }) =>
        record?.paymentPriority
          ? tEnums(
              `shipment_invoice_payment_priority_${record?.paymentPriority}`,
            )
          : '-',
    },
    {
      title: t('tabShipmentInvoiceTableShipmentInvoiceStatusColumn'),
      key: 'status',
      width: '11rem',
      render: ({ record }) => (record?.status ? statuses[record?.status] : '-'),
    },
    {
      title: '',
      key: 'actions',
      width: '6.5rem',
      render: ({ record }) => (
        <div className={styles['column-actions']}>
          <Button
            variant="secondary"
            model="round"
            size="small"
            onClick={() => handleOpenDrawerShow(record?.uuid || '')}
          >
            <EyeIcon />
          </Button>
          <Button
            variant="primary"
            model="round"
            size="small"
            onClick={() => handleOpenDrawerForm(record?.uuid || '')}
          >
            <EditIcon />
          </Button>
        </div>
      ),
    },
  ]

  const sorts = [
    {
      title: t('orderingCreatedAtButton'),
      sortBy: 'created_at',
    },
  ]

  const onChangeSelectedTab = (tab: string) => {
    setSelectedTab(tab)

    if (['next14Days', 'next30Days', 'today', 'next7Days'].includes(tab)) {
      let selectedDate = new Date()
      const dateNow = new Date()

      let expectedPaymentDateAfter = ''
      let expectedPaymentDateBefore = ''

      const days: Record<string, number> = {
        today: 0,
        next7Days: 7,
        next30Days: 30,
        next14Days: 14,
      }

      selectedDate = addDays(dateNow, days[tab])

      selectedDate.setHours(23, 59, 59, 999)

      dateNow.setHours(0, 0, 0, 0)

      expectedPaymentDateAfter = format(dateNow, "yyyy-MM-dd'T'HH:mm:ss'Z'")

      expectedPaymentDateBefore = format(
        selectedDate,
        "yyyy-MM-dd'T'HH:mm:ss'Z'",
      )

      setFilter({
        ...filter,
        expectedPaymentDateBefore,
        expectedPaymentDateAfter,
      })
    } else {
      setFilter({
        ...filter,
        expectedPaymentDateBefore: '',
        expectedPaymentDateAfter: '',
      })
    }
  }

  const onChangePeriod = (startDate?: string, endDate?: string) => {
    setFilter({
      ...filter,
      expectedPaymentDateAfter: startDate || '',
      expectedPaymentDateBefore: endDate || '',
    })
  }

  const ExpandedComponent = ({ record }: { record: ShipmentInvoiceProps }) => {
    return (
      <div className={styles['expanded-content']}>
        <div>
          <Text>
            <strong>
              {t(
                'tabShipmentInvoiceTableShipmentInvoiceInvoiceExternalReferenceLabel',
              )}
              :
            </strong>
          </Text>

          <Text>{record?.invoiceExternalReference || '-'}</Text>
        </div>
        <div>
          <Text>
            <strong>
              {t('tabShipmentInvoiceTableShipmentInvoiceObservationLabel')}:
            </strong>
          </Text>

          <Text>{record?.observation || '-'}</Text>
        </div>
      </div>
    )
  }

  useEffect(() => {
    if (data?.data?.count) {
      setTotalPages(
        Math.ceil(data?.data?.count / +(filter?.limit || DEFAULT_LIMIT_PAGE)),
      )
    }
  }, [data])

  return (
    <div>
      <div className={styles.tabs}>
        <Tab
          value={selectedTab || 'all'}
          onChange={onChangeSelectedTab}
          items={[
            {
              label: t('tabShipmentInvoiceTabAll'),
              value: 'all',
            },
            {
              label: t('tabShipmentInvoiceTabToday'),
              value: 'today',
            },
            {
              label: t('tabShipmentInvoiceTabNext7Days'),
              value: 'next7Days',
            },
            {
              label: t('tabShipmentInvoiceTabNext14Days'),
              value: 'next14Days',
            },
            {
              label: t('tabShipmentInvoiceTabNext30Days'),
              value: 'next30Days',
            },
            {
              label: t('tabShipmentInvoiceTabPeriod'),
              value: 'period',
            },
          ]}
        />

        {selectedTab === 'period' && (
          <PeriodDate
            onPeriodApply={onChangePeriod}
            initialEndDate={filter?.expectedPaymentDateBefore}
            initialStartDate={filter?.expectedPaymentDateAfter}
          />
        )}
      </div>

      <div className={styles.filter}>
        <div className={styles['filter-group']}>
          <InputSearch
            value={filter.search as string}
            onChange={(value) => handleFilter('search', value)}
            className={styles['search-input']}
            translations={{
              inputPlaceholder: t('tableSearchPlaceholder'),
            }}
          />

          <Button
            variant="secondary"
            onClick={() => setIsOpenDrawerFilter(true)}
          >
            <FilterIcon />
            {t('tableFilterButton')}
            {getContentTotalFiltered()}
          </Button>

          <SortBy
            sortBy={filter?.ordering}
            sorts={sorts}
            onHandleSortBy={onHandleSortBy}
          />
        </div>
      </div>

      <div className={styles['filtered-options']}>
        <AppliedFilters
          filtersApplied={filter}
          excludeKeys={[
            'limit',
            'page',
            'ordering',
            'uuid',
            'showUuid',
            'expectedPaymentDateBefore',
            'expectedPaymentDateAfter',
          ]}
          onRemoveFilter={(key) => handleFilter(key, '')}
          translations={{
            filterAppliedText: t('filterApplied'),
          }}
          keyTranslations={{
            currency: t('tableShipmentInvoiceDrawerFilterCurrency'),
            status: t('tableShipmentInvoiceDrawerFilterStatus'),
            transportCompany_Email: t(
              'tableShipmentInvoiceDrawerFilterTransportCompany',
            ),
            paymentPriority: t(
              'tableShipmentInvoiceDrawerFilterPaymentPriority',
            ),
          }}
          renderValue={(key, value) => {
            if (key === 'status') {
              return tEnums(`shipment_invoice_status_${value}`)
            }

            if (key === 'paymentPriority') {
              return tEnums(`shipment_invoice_payment_priority_${value}`)
            }

            if (key === 'transportCompany_Email') {
              return (
                transportCompanyData?.data?.results?.find(
                  (transportCompany) => transportCompany.email === value,
                )?.name || value
              )
            }

            return value
          }}
        />
      </div>

      <TableColumns<ShipmentInvoiceProps>
        isLoading={isLoading}
        columns={columns}
        data={data?.data?.results || []}
        onSortBy={onHandleSortBy}
        sortBy={filter?.ordering}
        expandableComponent={ExpandedComponent}
      />

      {!isLoading && (!data?.data || data?.data?.results?.length === 0) && (
        <EmptyState
          title={t('tabShipmentInvoiceTableShipmentInvoicEmptyTitle')}
          description={t(
            'tabShipmentInvoiceTableShipmentInvoicEmptyDescription',
          )}
        />
      )}

      {data?.data?.results && data.data.results.length >= 1 && (
        <div className={styles.pagination}>
          <div className={styles['pagination-show-registers']}>
            <Text>
              {t('tableShowRegisters', {
                showNumber:
                  (data?.data?.count || 0) < +filter.limit
                    ? data?.data?.count
                    : +filter.limit,
                totalNumber: data?.data?.count || 0,
              })}
            </Text>

            <Select
              value={filter.limit}
              onChange={(value) => handleFilter('limit', value)}
              options={[
                { label: '10', value: '10' },
                { label: '20', value: '20' },
                { label: '50', value: '50' },
                { label: '100', value: '100' },
              ]}
            />
          </div>

          <Pagination
            totalPages={totalPages}
            onChange={(page) => handleFilter('page', page)}
            currentPage={+filter.page as number}
          />
        </div>
      )}

      <ShipmentInvoiceDrawerFilter
        filter={filter}
        onHandleFilter={onChangeFilter}
        isOpen={isOpenDrawerFilter}
        onHandleOpen={() => setIsOpenDrawerFilter(false)}
        transportCompanies={transportCompanyData?.data?.results || []}
      />

      <ShipmentInvoiceDrawerForm
        uuid={params.uuid}
        onHandleOpen={() => setParams({ uuid: '' })}
      />

      <ShipmentInvoiceDrawerShow
        uuid={params.showUuid}
        onHandleOpen={() => setParams({ showUuid: '' })}
      />

      <ShipmentInvoiceDrawerRequestNew
        isOpen={isOpenNewShipmentInvoice}
        onHandleOpen={onHandleOpenNewShipmentInvoice}
      />
    </div>
  )
}
